/**
 * PendingAISuggestionsWidget
 * 
 * Dashboard widget showing AI-parsed client responses awaiting specialist approval.
 * Displays transaction details, AI suggestions with confidence scores, and bulk actions.
 * 
 * KEYBOARD SHORTCUTS (when widget has focus):
 * - A: Approve focused item
 * - R: Reject/skip focused item  
 * - E: Edit category (navigate to wizard)
 * - N/↓: Next item
 * - P/↑: Previous item
 * - Esc: Clear focus
 * - ?: Show shortcuts help
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../../utils/api';
import {
  Bot,
  CheckCircle2,
  XCircle,
  ChevronDown,
  ChevronUp,
  Sparkles,
  AlertCircle,
  RefreshCw,
  ExternalLink,
  Keyboard
} from 'lucide-react';

interface ParsedResponse {
  transaction_id: string;
  report_id?: string;
  claim_id?: string;
  claimant_name?: string;
  original_question?: string;
  client_response?: string;
  parsed_category?: string;
  ai_parsed_category?: string;
  ai_confidence?: number;
  ai_reasoning?: string;
  ai_needs_human_review?: boolean;
  amount?: number;
  date?: string;
  description?: string;
  status?: string;
}

interface PendingData {
  total_pending: number;
  needs_parsing: ParsedResponse[];
  needs_approval: ParsedResponse[];
  summary: {
    awaiting_ai_parsing: number;
    awaiting_specialist_approval: number;
  };
}

export default function PendingAISuggestionsWidget() {
  const navigate = useNavigate();
  const widgetRef = useRef<HTMLDivElement>(null);
  
  const [loading, setLoading] = useState(true);
  const [pendingData, setPendingData] = useState<PendingData | null>(null);
  const [expanded, setExpanded] = useState(true);
  const [selectedItems, setSelectedItems] = useState<Set<string>>(new Set());
  const [approving, setApproving] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [parsingAll, setParsingAll] = useState(false);
  const [parseProgress, setParseProgress] = useState<string | null>(null);
  
  // Keyboard navigation state
  const [focusedIndex, setFocusedIndex] = useState<number>(-1);
  const [showShortcutsHelp, setShowShortcutsHelp] = useState(false);
  const [announcement, setAnnouncement] = useState<string>('');

  const fetchPendingResponses = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Fetch pending AI suggestions from dedicated endpoint
      const data = await api.get('/api/v1/clarifications/pending-ai-suggestions?limit=50', {
        showErrorToast: false // Silent fail for optional widget
      });
      
      setPendingData({
        total_pending: data.total_pending || 0,
        needs_parsing: data.needs_parsing || [],
        needs_approval: data.needs_approval || [],
        summary: data.summary || {
          awaiting_ai_parsing: 0,
          awaiting_specialist_approval: 0
        }
      });
      
    } catch (err) {
      console.error('Error fetching pending responses:', err);
      // Silently fail - don't show error for optional widget
      setPendingData(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPendingResponses();
  }, [fetchPendingResponses]);

  const handleApprove = async (item: ParsedResponse) => {
    if (!item.ai_parsed_category) return;
    
    setApproving(item.transaction_id);
    
    try {
      await api.post(
        `/api/v1/assistant/actions/apply-parsed-category?` + 
        `claim_id=${item.claim_id}&` +
        `transaction_id=${encodeURIComponent(item.transaction_id)}&` +
        `category=${encodeURIComponent(item.ai_parsed_category)}&` +
        `specialist_confirmed=true`,
        null,
        { showErrorToast: false }
      );
      
      // Remove from list
      setPendingData(prev => {
        if (!prev) return prev;
        return {
          ...prev,
          needs_approval: prev.needs_approval.filter(i => i.transaction_id !== item.transaction_id),
          total_pending: prev.total_pending - 1,
          summary: {
            ...prev.summary,
            awaiting_specialist_approval: prev.summary.awaiting_specialist_approval - 1
          }
        };
      });
      
    } catch (err) {
      console.error('Error approving:', err);
      setError(err instanceof Error ? err.message : 'Failed to approve');
    } finally {
      setApproving(null);
    }
  };

  const handleBulkApprove = async () => {
    const itemsToApprove = pendingData?.needs_approval.filter(
      item => selectedItems.has(item.transaction_id) && item.ai_confidence && item.ai_confidence >= 0.8
    ) || [];
    
    for (const item of itemsToApprove) {
      await handleApprove(item);
    }
    
    setSelectedItems(new Set());
  };

  const handleParseAll = async () => {
    if (parsingAll) return;
    
    setParsingAll(true);
    setParseProgress('Starting batch parse...');
    setError(null);
    
    try {
      setParseProgress(`Parsing ${pendingData?.summary.awaiting_ai_parsing || 0} responses...`);
      
      const result = await api.post('/api/v1/assistant/actions/parse-all-pending', null, {
        showErrorToast: true
      });
      
      setParseProgress(null);
      announce(`Parsed ${result.total_processed} responses: ${result.high_confidence} ready to approve`);
      
      // Refresh the data to show new suggestions
      await fetchPendingResponses();
      
    } catch (err) {
      console.error('Parse all error:', err);
      setError(err instanceof Error ? err.message : 'Failed to parse responses');
      setParseProgress(null);
    } finally {
      setParsingAll(false);
    }
  };

  const getConfidenceColor = (confidence: number) => {
    if (confidence >= 0.9) return 'text-green-600 bg-green-100';
    if (confidence >= 0.7) return 'text-yellow-600 bg-yellow-100';
    return 'text-red-600 bg-red-100';
  };

  const getConfidenceIcon = (confidence: number) => {
    if (confidence >= 0.9) return '🟢';
    if (confidence >= 0.7) return '🟡';
    return '🔴';
  };

  // Get focused item from needs_approval list
  const focusedItem = pendingData?.needs_approval[focusedIndex];

  // Announce for screen readers
  const announce = useCallback((message: string) => {
    setAnnouncement(message);
    setTimeout(() => setAnnouncement(''), 1000);
  }, []);

  // Keyboard shortcuts handler
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Only handle if widget is expanded and not typing in an input
      if (!expanded || !pendingData) return;
      const target = e.target as HTMLElement;
      if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable) return;
      
      const items = pendingData.needs_approval;
      if (items.length === 0) return;

      switch (e.key.toLowerCase()) {
        case 'a':
          // Approve focused item
          if (focusedIndex >= 0 && focusedItem) {
            e.preventDefault();
            handleApprove(focusedItem);
            announce(`Approved: ${focusedItem.ai_parsed_category}`);
            // Move to next item
            if (focusedIndex < items.length - 1) {
              setFocusedIndex(prev => prev); // Stay in place as item will be removed
            } else {
              setFocusedIndex(Math.max(0, items.length - 2));
            }
          }
          break;
        
        case 'r':
          // Reject/skip - just move to next
          if (focusedIndex >= 0) {
            e.preventDefault();
            announce('Skipped');
            setFocusedIndex(prev => Math.min(prev + 1, items.length - 1));
          }
          break;
        
        case 'e':
          // Edit - navigate to wizard
          if (focusedIndex >= 0 && focusedItem?.claim_id) {
            e.preventDefault();
            navigate(`/wizard-v2?draft=${focusedItem.claim_id}&step=3&section=transactions`);
          }
          break;
        
        case 'n':
        case 'arrowdown':
          // Next item
          e.preventDefault();
          setFocusedIndex(prev => {
            const next = Math.min(prev + 1, items.length - 1);
            if (next !== prev && items[next]) {
              announce(`${items[next].claimant_name}: ${items[next].description}`);
            }
            return next;
          });
          break;
        
        case 'p':
        case 'arrowup':
          // Previous item
          e.preventDefault();
          setFocusedIndex(prev => {
            const next = Math.max(prev - 1, 0);
            if (next !== prev && items[next]) {
              announce(`${items[next].claimant_name}: ${items[next].description}`);
            }
            return next;
          });
          break;
        
        case 'escape':
          // Clear focus
          e.preventDefault();
          setFocusedIndex(-1);
          setShowShortcutsHelp(false);
          announce('Focus cleared');
          break;
        
        case '?':
          // Toggle shortcuts help
          e.preventDefault();
          setShowShortcutsHelp(prev => !prev);
          break;
        
        case ' ':
          // Space to select/deselect for bulk
          if (focusedIndex >= 0 && focusedItem) {
            e.preventDefault();
            setSelectedItems(prev => {
              const next = new Set(prev);
              if (next.has(focusedItem.transaction_id)) {
                next.delete(focusedItem.transaction_id);
                announce('Deselected');
              } else {
                next.add(focusedItem.transaction_id);
                announce('Selected');
              }
              return next;
            });
          }
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [expanded, pendingData, focusedIndex, focusedItem, navigate, announce]);

  // Don't show if no pending items
  if (!loading && (!pendingData || pendingData.total_pending === 0)) {
    return null;
  }

  return (
    <div 
      ref={widgetRef}
      className="bg-gradient-to-r from-blue-50 to-sky-50 border border-blue-200 rounded-xl p-4 mb-6" 
      data-testid="pending-ai-suggestions-widget"
      tabIndex={0}
      onFocus={() => focusedIndex === -1 && pendingData?.needs_approval.length && setFocusedIndex(0)}
    >
      {/* ARIA Live Region for announcements */}
      <div aria-live="polite" aria-atomic="true" className="sr-only">
        {announcement}
      </div>

      {/* Keyboard Shortcuts Help Modal */}
      {showShortcutsHelp && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setShowShortcutsHelp(false)}>
          <div className="bg-white rounded-xl shadow-xl p-6 max-w-sm mx-4" onClick={e => e.stopPropagation()}>
            <div className="flex items-center gap-2 mb-4">
              <Keyboard className="w-5 h-5 text-blue-600" />
              <h3 className="font-semibold text-gray-900">Keyboard Shortcuts</h3>
            </div>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between"><span className="text-gray-600">Approve</span><kbd className="px-2 py-1 bg-gray-100 rounded text-xs font-mono">A</kbd></div>
              <div className="flex justify-between"><span className="text-gray-600">Skip/Reject</span><kbd className="px-2 py-1 bg-gray-100 rounded text-xs font-mono">R</kbd></div>
              <div className="flex justify-between"><span className="text-gray-600">Edit in Wizard</span><kbd className="px-2 py-1 bg-gray-100 rounded text-xs font-mono">E</kbd></div>
              <div className="flex justify-between"><span className="text-gray-600">Next item</span><kbd className="px-2 py-1 bg-gray-100 rounded text-xs font-mono">N</kbd> or <kbd className="px-2 py-1 bg-gray-100 rounded text-xs font-mono">↓</kbd></div>
              <div className="flex justify-between"><span className="text-gray-600">Previous item</span><kbd className="px-2 py-1 bg-gray-100 rounded text-xs font-mono">P</kbd> or <kbd className="px-2 py-1 bg-gray-100 rounded text-xs font-mono">↑</kbd></div>
              <div className="flex justify-between"><span className="text-gray-600">Select for bulk</span><kbd className="px-2 py-1 bg-gray-100 rounded text-xs font-mono">Space</kbd></div>
              <div className="flex justify-between"><span className="text-gray-600">Clear focus</span><kbd className="px-2 py-1 bg-gray-100 rounded text-xs font-mono">Esc</kbd></div>
              <div className="flex justify-between"><span className="text-gray-600">Show this help</span><kbd className="px-2 py-1 bg-gray-100 rounded text-xs font-mono">?</kbd></div>
            </div>
            <button 
              onClick={() => setShowShortcutsHelp(false)}
              className="mt-4 w-full py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              Got it
            </button>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <button
          onClick={() => setExpanded(!expanded)}
          className="flex items-center gap-2 hover:opacity-80 transition-opacity"
        >
          <div className="p-2 bg-blue-100 rounded-lg">
            <Bot className="w-5 h-5 text-blue-600" />
          </div>
          <div className="text-left">
            <h3 className="font-semibold text-blue-900 flex items-center gap-2">
              Pending AI Suggestions
              {loading ? (
                <RefreshCw className="w-4 h-4 animate-spin text-blue-400" />
              ) : (
                <span className="text-sm font-normal text-blue-600 bg-blue-100 px-2 py-0.5 rounded-full">
                  {pendingData?.total_pending || 0}
                </span>
              )}
            </h3>
            <p className="text-xs text-blue-600">
              Client responses ready for review
            </p>
          </div>
          {expanded ? (
            <ChevronUp className="w-5 h-5 text-blue-400" />
          ) : (
            <ChevronDown className="w-5 h-5 text-blue-400" />
          )}
        </button>
        
        <div className="flex items-center gap-2">
          {/* Keyboard shortcut hint */}
          <button
            onClick={() => setShowShortcutsHelp(true)}
            className="p-2 text-blue-400 hover:text-blue-600 hover:bg-blue-100 rounded-lg transition-colors"
            title="Keyboard shortcuts (?)"
          >
            <Keyboard className="w-4 h-4" />
          </button>
          {/* Parse All button - only show if there are unparsed responses */}
          {pendingData && pendingData.summary.awaiting_ai_parsing > 0 && (
            <button
              onClick={handleParseAll}
              disabled={parsingAll}
              className="px-3 py-1.5 text-sm font-medium text-blue-700 bg-blue-100 hover:bg-blue-200 rounded-lg flex items-center gap-1 transition-colors disabled:opacity-50"
              title="Parse all pending responses with AI"
            >
              {parsingAll ? (
                <>
                  <RefreshCw className="w-4 h-4 animate-spin" />
                  Parsing...
                </>
              ) : (
                <>
                  <Sparkles className="w-4 h-4" />
                  Parse All ({pendingData.summary.awaiting_ai_parsing})
                </>
              )}
            </button>
          )}
          {selectedItems.size > 0 && (
            <button
              onClick={handleBulkApprove}
              className="px-3 py-1.5 text-sm font-medium text-green-700 bg-green-100 hover:bg-green-200 rounded-lg flex items-center gap-1 transition-colors"
            >
              <CheckCircle2 className="w-4 h-4" />
              Approve ({selectedItems.size})
            </button>
          )}
          <button
            onClick={fetchPendingResponses}
            disabled={loading}
            className="p-2 text-blue-400 hover:text-blue-600 hover:bg-blue-100 rounded-lg transition-colors"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {/* Progress indicator for batch parsing */}
      {parseProgress && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-3 flex items-center gap-2">
          <RefreshCw className="w-4 h-4 text-blue-500 animate-spin" />
          <span className="text-sm text-blue-700">{parseProgress}</span>
        </div>
      )}

      {/* Error */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-3 flex items-center gap-2">
          <AlertCircle className="w-4 h-4 text-red-500" />
          <span className="text-sm text-red-700">{error}</span>
        </div>
      )}

      {/* Content */}
      {expanded && pendingData && (
        <div className="space-y-2">
          {/* Summary Stats */}
          <div className="flex gap-4 mb-3">
            {pendingData.summary.awaiting_specialist_approval > 0 && (
              <div className="flex items-center gap-2 text-sm">
                <Sparkles className="w-4 h-4 text-blue-500" />
                <span className="text-blue-700">
                  <strong>{pendingData.summary.awaiting_specialist_approval}</strong> ready to approve
                </span>
              </div>
            )}
            {pendingData.summary.awaiting_ai_parsing > 0 && (
              <div className="flex items-center gap-2 text-sm">
                <AlertCircle className="w-4 h-4 text-amber-500" />
                <span className="text-amber-700">
                  <strong>{pendingData.summary.awaiting_ai_parsing}</strong> need AI parsing
                </span>
              </div>
            )}
          </div>

          {/* Items needing approval */}
          {pendingData.needs_approval.map((item, idx) => (
            <div
              key={item.transaction_id}
              className={`bg-white rounded-lg border p-3 transition-all ${
                focusedIndex === idx
                  ? 'border-blue-500 bg-blue-50 ring-2 ring-blue-300 ring-offset-1'
                  : selectedItems.has(item.transaction_id)
                  ? 'border-blue-400 bg-blue-50'
                  : 'border-blue-100 hover:border-blue-300'
              }`}
              onClick={() => setFocusedIndex(idx)}
            >
              <div className="flex items-start gap-3">
                {/* Focus indicator + Checkbox */}
                <div className="flex items-center gap-2">
                  {focusedIndex === idx && (
                    <div className="w-1 h-8 bg-blue-500 rounded-full" />
                  )}
                  <input
                    type="checkbox"
                    checked={selectedItems.has(item.transaction_id)}
                    onChange={() => {
                      setSelectedItems(prev => {
                        const next = new Set(prev);
                        if (next.has(item.transaction_id)) {
                          next.delete(item.transaction_id);
                        } else {
                          next.add(item.transaction_id);
                        }
                        return next;
                      });
                    }}
                    className="mt-1 w-4 h-4 rounded border-blue-300 text-blue-600 focus:ring-blue-500"
                  />
                </div>
                
                {/* Content */}
                <div className="flex-1 min-w-0">
                  {/* Claim & Transaction Info */}
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-medium text-gray-900 truncate">
                      {item.claimant_name || 'Unknown Claim'}
                    </span>
                    <span className="text-gray-400">•</span>
                    <span className="text-sm text-gray-500 truncate">
                      {item.description || item.transaction_id}
                    </span>
                    {item.amount && (
                      <>
                        <span className="text-gray-400">•</span>
                        <span className={`text-sm font-medium ${item.amount < 0 ? 'text-red-600' : 'text-green-600'}`}>
                          ${Math.abs(item.amount).toLocaleString()}
                        </span>
                      </>
                    )}
                  </div>
                  
                  {/* Client Response */}
                  <p className="text-sm text-gray-600 mb-2 line-clamp-2">
                    <span className="font-medium text-gray-700">Client:</span> {item.client_response}
                  </p>
                  
                  {/* AI Suggestion */}
                  {item.ai_parsed_category && (
                    <div className="flex items-center gap-3">
                      <div className="flex items-center gap-2">
                        <Bot className="w-4 h-4 text-blue-500" />
                        <span className="text-sm font-medium text-blue-700">
                          {item.ai_parsed_category}
                        </span>
                      </div>
                      {item.ai_confidence && (
                        <span className={`text-xs px-2 py-0.5 rounded-full ${getConfidenceColor(item.ai_confidence)}`}>
                          {getConfidenceIcon(item.ai_confidence)} {Math.round(item.ai_confidence * 100)}%
                        </span>
                      )}
                      {item.ai_reasoning && (
                        <span className="text-xs text-gray-500 truncate max-w-xs" title={item.ai_reasoning}>
                          {item.ai_reasoning.slice(0, 50)}...
                        </span>
                      )}
                    </div>
                  )}
                </div>
                
                {/* Actions */}
                <div className="flex items-center gap-2 flex-shrink-0">
                  <button
                    onClick={() => navigate(`/wizard-v2?draft=${item.claim_id}&step=3&section=transactions`)}
                    className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                    title="View in Wizard (E)"
                  >
                    <ExternalLink className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => handleApprove(item)}
                    disabled={approving === item.transaction_id || !item.ai_parsed_category}
                    className="px-3 py-1.5 text-sm font-medium bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
                    title="Approve (A)"
                  >
                    {approving === item.transaction_id ? (
                      <RefreshCw className="w-4 h-4 animate-spin" />
                    ) : (
                      <CheckCircle2 className="w-4 h-4" />
                    )}
                    Approve
                    {focusedIndex === idx && (
                      <kbd className="ml-1 px-1 py-0.5 text-[10px] bg-green-700 rounded">A</kbd>
                    )}
                  </button>
                </div>
              </div>
            </div>
          ))}

          {/* Items needing parsing (show fewer details) */}
          {pendingData.needs_parsing.length > 0 && (
            <div className="mt-4">
              <h4 className="text-sm font-medium text-amber-700 mb-2 flex items-center gap-2">
                <AlertCircle className="w-4 h-4" />
                Awaiting AI Parsing ({pendingData.needs_parsing.length})
              </h4>
              <div className="bg-amber-50 rounded-lg border border-amber-200 p-3">
                <p className="text-sm text-amber-700 mb-2">
                  These responses need to be parsed by AI. Click "Parse All" above, or open the QA Assistant in a claim wizard.
                </p>
                <div className="flex flex-wrap gap-2">
                  {/* Group by claim to show unique claims only */}
                  {Array.from(new Set(pendingData.needs_parsing.map(item => item.claim_id))).slice(0, 5).map((claimId) => {
                    const item = pendingData.needs_parsing.find(i => i.claim_id === claimId);
                    return (
                      <button
                        key={claimId}
                        onClick={() => navigate(`/wizard-v2?draft=${claimId}&step=3`)}
                        className="px-3 py-1 text-xs font-medium text-amber-700 bg-white border border-amber-300 rounded-full hover:bg-amber-100 transition-colors"
                        title={`Claim ID: ${claimId}`}
                      >
                        {item?.claimant_name || claimId?.slice(0, 12) + '...'}
                      </button>
                    );
                  })}
                  {new Set(pendingData.needs_parsing.map(item => item.claim_id)).size > 5 && (
                    <span className="px-3 py-1 text-xs text-amber-600">
                      +{new Set(pendingData.needs_parsing.map(item => item.claim_id)).size - 5} more claims
                    </span>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Empty State */}
          {pendingData.needs_approval.length === 0 && pendingData.needs_parsing.length === 0 && (
            <div className="text-center py-6 text-blue-600">
              <CheckCircle2 className="w-8 h-8 mx-auto mb-2 text-blue-400" />
              <p className="text-sm">All caught up! No pending AI suggestions.</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
